import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "login-modal__header" }
const _hoisted_2 = { class: "login-modal__title" }
const _hoisted_3 = {
  key: 0,
  class: "login-modal__description"
}
const _hoisted_4 = {
  key: 2,
  class: "login-modal__recover-error"
}
const _hoisted_5 = {
  key: 5,
  class: "login-modal__options"
}
const _hoisted_6 = { class: "login-modal__recover" }
const _hoisted_7 = ["disabled"]

import { computed, nextTick, reactive, ref, watch, h } from 'vue';
import { message } from 'ant-design-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';

import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { trackYaGoal } from '@/js/utils/yandex-metrika';

const LOGIN_ENDPOINT = '/api/auth/login/';
const RESET_PASSWORD_ENDPOINT = '/api/auth/reset-password/';
const RECOVER_SUCCESS_FALLBACK_DESCRIPTION =
  'Если указанный email зарегистрирован, мы отправили ссылку для смены пароля';
const LOGIN_BLOCK_ERROR_FALLBACK =
  'Ваш аккаунт временно заблокирован на 15 минут минут из-за превышения количества неверных авторизаций.';

export default /*@__PURE__*/_defineComponent({
  __name: 'login-modal',
  setup(__props, { expose: __expose }) {

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

const isOpen = ref(false);
const mode = ref<'login' | 'recover'>('login');
const submitLoading = ref(false);
const loginErrorMessage = ref('');
const recoverErrorMessage = ref('');
const recoverSuccessModalOpen = ref(false);
const recoverSuccessDescription = ref('');
const formRef = ref<FormInstance>();

const form = reactive({
  password: '',
  login: '',
  email: '',
  rememberMe: false,
});

const authValidator = async () => {
  if (mode.value === 'login' && loginErrorMessage.value) {
    return Promise.reject();
  }
  if (mode.value === 'recover' && recoverErrorMessage.value) {
    return Promise.reject();
  }
  return Promise.resolve();
};

const rules = computed<Record<string, Rule[]>>(() => {
  if (mode.value === 'login') {
    return {
      login: [{ required: true, message: 'Введите логин' }, { validator: authValidator }],
      password: [{ required: true, message: 'Введите пароль' }, { validator: authValidator }],
    } as Record<string, Rule[]>;
  }
  return {
    email: [
      { required: true, message: 'Введите почту' },
      { type: 'email', message: 'Введите корректный email' },
      { validator: authValidator },
    ],
  } as Record<string, Rule[]>;
});

const isLoginMode = computed(() => mode.value === 'login');
const isRecoverMode = computed(() => mode.value === 'recover');
const modalTitle = computed(() => (isRecoverMode.value ? 'Восстановить пароль' : 'Войти'));
const submitButtonText = computed(() => (isRecoverMode.value ? 'Отправить ссылку' : 'Войти'));
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const canSubmit = computed(() =>
  isRecoverMode.value
    ? form.email.trim().length > 0 && isValidEmail(form.email) && !recoverErrorMessage.value
    : form.login.trim().length > 0 && form.password.trim().length > 0 && !loginErrorMessage.value,
);

const readApiErrorMessage = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const record = data as Record<string, unknown>;
  const candidates = ['message', 'detail', 'error', 'msg'];
  for (const key of candidates) {
    const value = record[key] as { code?: Number; error?: string } | string;
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    } else if (
      typeof value !== 'string' &&
      key === 'msg' &&
      value?.code === 400 &&
      value?.error?.trim()
    ) {
      return value.error.trim();
    } else if (typeof value !== 'string' && key === 'msg' && value?.code === 423) {
      return LOGIN_BLOCK_ERROR_FALLBACK;
    }
  }
  if (Array.isArray(record.detail) && record.detail.every((item) => typeof item === 'string')) {
    return record.detail.filter(Boolean).join(' ');
  }
  return null;
};

const readResetPasswordSuccessMsg = (data: unknown): string => {
  if (!data || typeof data !== 'object') {
    return '';
  }
  const msg = (data as Record<string, unknown>).msg;
  return typeof msg === 'string' ? msg.trim() : '';
};

const parseResponseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const clearLoginError = () => {
  loginErrorMessage.value = '';
  formRef.value?.clearValidate();
};

const clearRecoverError = () => {
  recoverErrorMessage.value = '';
  formRef.value?.clearValidate();
};

const resetFormState = () => {
  form.password = '';
  form.login = '';
  form.email = '';
  form.rememberMe = false;
  submitLoading.value = false;
  nextTick(() => {
    formRef.value?.clearValidate();
  });
};

watch(
  () => [form.login, form.password],
  () => {
    clearLoginError();
  },
);

watch(
  () => form.email,
  () => {
    clearRecoverError();
  },
);

const onSubmit = async () => {
  clearLoginError();
  clearRecoverError();
  submitLoading.value = true;
  try {
    if (isRecoverMode.value) {
      const response = await fetch(getApiUrl(RESET_PASSWORD_ENDPOINT), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email: form.email.trim() }),
      });

      const data = await parseResponseBody(response);

      if (!response.ok) {
        const fromBody = readApiErrorMessage(data);
        throw new Error(fromBody ?? `Ошибка отправки (${response.status})`);
      }

      recoverSuccessDescription.value =
        readResetPasswordSuccessMsg(data) || RECOVER_SUCCESS_FALLBACK_DESCRIPTION;
      close(true);
      recoverSuccessModalOpen.value = true;
      return;
    }

    const response = await fetch(getApiUrl(LOGIN_ENDPOINT), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        email: form.login.trim(),
        password: form.password,
        remember: form.rememberMe ? 'Y' : 'N',
      }),
    });

    const data = await parseResponseBody(response);

    if (!response.ok) {
      const fromBody = readApiErrorMessage(data);
      throw new Error(fromBody ?? `Ошибка входа (${response.status})`);
    }

    message.success('Вход выполнен');
    trackYaGoal('lk');
    close();
    const redirect = (data as { msg?: { redirectTo?: string } })?.msg?.redirectTo;
    if (redirect) {
      window.location.replace(redirect);
      return;
    }
    window.location.reload();
  } catch (err) {
    const fallback = isRecoverMode.value
      ? 'Не удалось отправить ссылку'
      : 'Не удалось выполнить вход';
    if (isRecoverMode.value) {
      recoverErrorMessage.value = err instanceof Error ? err.message : fallback;
      await formRef.value?.validate();
      return;
    }
    if (isLoginMode.value) {
      loginErrorMessage.value = err instanceof Error ? err.message : fallback;
      await formRef.value?.validate();
      return;
    }
  } finally {
    submitLoading.value = false;
  }
};

const switchToRecoverMode = () => {
  clearLoginError();
  clearRecoverError();
  mode.value = 'recover';
};

const switchToLoginMode = () => {
  clearLoginError();
  clearRecoverError();
  mode.value = 'login';
};

const open = () => {
  resetFormState();
  switchToLoginMode();
  isOpen.value = true;
};

const onRecoverSuccessModalOpenUpdate = (open: boolean) => {
  recoverSuccessModalOpen.value = open;
  if (!open) {
    switchToLoginMode();
    recoverSuccessDescription.value = '';
  }
};

const close = (notClearRecoverDescription?: boolean) => {
  clearLoginError();
  clearRecoverError();
  resetFormState();
  recoverSuccessModalOpen.value = false;
  if (!notClearRecoverDescription) {
    recoverSuccessDescription.value = '';
  }
  switchToLoginMode();
  isOpen.value = false;
};

const renderPasswordIcon = (visible: boolean) =>
  h('img', {
    src: visible ? `${ICONS_BASE}/share-icons/eye-slash.svg` : `${ICONS_BASE}/share-icons/eye.svg`,
    alt: '',
    width: 24,
    height: 24,
  });

__expose({ open, close });

return (_ctx: any,_cache: any) => {
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_input_password = _resolveComponent("a-input-password")!
  const _component_a_alert = _resolveComponent("a-alert")!
  const _component_a_checkbox = _resolveComponent("a-checkbox")!
  const _component_botton = _resolveComponent("botton")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_modal = _resolveComponent("a-modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_a_modal, {
      open: isOpen.value,
      "onUpdate:open": _cache[4] || (_cache[4] = ($event: any) => ((isOpen).value = $event)),
      class: "login-modal",
      closable: true,
      "mask-closable": false,
      keyboard: false,
      footer: null,
      onCancel: close,
      centered: "",
      "wrap-class-name": "full-modal-login"
    }, {
      title: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(modalTitle.value), 1),
          (isRecoverMode.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, " Укажите почтовый адрес, на который выслать ссылку для изменения пароля. "))
            : _createCommentVNode("", true)
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_a_form, {
          ref_key: "formRef",
          ref: formRef,
          class: _normalizeClass(["login-modal__form stc-ant-field-skin", { 'login-modal__form--recover': isRecoverMode.value }]),
          layout: "vertical",
          "required-mark": false,
          model: form,
          rules: rules.value,
          onFinish: onSubmit
        }, {
          default: _withCtx(() => [
            (isLoginMode.value)
              ? (_openBlock(), _createBlock(_component_a_form_item, {
                  key: 0,
                  name: "login",
                  label: "Логин",
                  class: "stc-ant-field-skin"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.login,
                      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.login) = $event)),
                      size: "large",
                      placeholder: "Логин",
                      autocomplete: "username"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (isRecoverMode.value)
              ? (_openBlock(), _createBlock(_component_a_form_item, {
                  key: 1,
                  name: "email",
                  label: "Почта",
                  class: "stc-ant-field-skin"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.email,
                      "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((form.email) = $event)),
                      size: "large",
                      type: "email",
                      placeholder: "Введите почту",
                      autocomplete: "email"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (isRecoverMode.value && recoverErrorMessage.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _cache[5] || (_cache[5] = _createElementVNode("span", {
                    class: "login-modal__recover-error-icon",
                    "aria-hidden": "true"
                  }, [
                    _createElementVNode("svg", {
                      class: "login-modal__recover-error-mark",
                      width: "14",
                      height: "14",
                      viewBox: "0 0 14 14",
                      fill: "none",
                      xmlns: "http://www.w3.org/2000/svg"
                    }, [
                      _createElementVNode("circle", {
                        cx: "7",
                        cy: "7",
                        r: "7",
                        fill: "var(--stc-red-600)"
                      }),
                      _createElementVNode("path", {
                        d: "M4.25 4.25 L9.75 9.75 M9.75 4.25 L4.25 9.75",
                        stroke: "#fff",
                        "stroke-width": "1.35",
                        "stroke-linecap": "round"
                      })
                    ])
                  ], -1)),
                  _createElementVNode("span", null, _toDisplayString(recoverErrorMessage.value), 1)
                ]))
              : _createCommentVNode("", true),
            (isLoginMode.value)
              ? (_openBlock(), _createBlock(_component_a_form_item, {
                  key: 3,
                  name: "password",
                  label: "Пароль",
                  class: "stc-ant-field-skin"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input_password, {
                      value: form.password,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((form.password) = $event)),
                      size: "large",
                      placeholder: "Введите пароль",
                      autocomplete: "current-password",
                      "icon-render": renderPasswordIcon
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (isLoginMode.value && loginErrorMessage.value)
              ? (_openBlock(), _createBlock(_component_a_alert, {
                  key: 4,
                  class: "login-modal__auth-error",
                  type: "error",
                  message: loginErrorMessage.value,
                  "show-icon": ""
                }, null, 8, ["message"]))
              : _createCommentVNode("", true),
            (isLoginMode.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(_component_a_checkbox, {
                    checked: form.rememberMe,
                    "onUpdate:checked": _cache[3] || (_cache[3] = ($event: any) => ((form.rememberMe) = $event))
                  }, {
                    default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                      _createTextVNode("Запомнить меня", -1)
                    ]))]),
                    _: 1
                  }, 8, ["checked"]),
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_component_botton, {
                      onClick: _withModifiers(switchToRecoverMode, ["prevent"])
                    }, {
                      default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                        _createTextVNode("Забыли пароль?", -1)
                      ]))]),
                      _: 1
                    })
                  ])
                ]))
              : _createCommentVNode("", true),
            (isRecoverMode.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 6,
                  type: "button",
                  class: "login-modal__back",
                  onClick: switchToLoginMode
                }, " Вернуться ко входу "))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              type: "submit",
              class: "btn btn-primary btn-medium login-modal__footer",
              disabled: !canSubmit.value || submitLoading.value
            }, _toDisplayString(submitButtonText.value), 9, _hoisted_7)
          ]),
          _: 1
        }, 8, ["class", "model", "rules"])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(ConsultationRequestSuccessModal, {
      open: recoverSuccessModalOpen.value,
      title: "Заявка отправлена",
      description: recoverSuccessDescription.value,
      "onUpdate:open": onRecoverSuccessModalOpenUpdate
    }, null, 8, ["open", "description"])
  ], 64))
}
}

})